import { useEffect, useState } from 'react'

import { QuickFillButton } from './quick-fill-button'
import type { StocktakeLine } from '@/lib/api/stocktake-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

const WARNING_THRESHOLD = 0.1

function formatSigned(n: number) {
    return n > 0 ? `+${formatQty(n)}` : formatQty(n)
}

interface Props {
    line: StocktakeLine
    index: number
    editable: boolean
    onChange: (
        data: {
            counted_qty?: number | null
            line_notes?: string | null
        },
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
}

export function StocktakeLineCard({
    line,
    index,
    editable,
    onChange: submitChange,
}: Props) {
    const workflow = useWorkflowTracking('stocktake_count', {
        operation: 'update',
        active: editable,
        attemptKey: line.id,
        autoStart: false,
    })
    const onChange = (data: Parameters<Props['onChange']>[0]) => {
        workflow.interact('items')
        submitChange(data, workflow)
    }
    const [countedStr, setCountedStr] = useState(
        line.counted_qty != null ? String(line.counted_qty) : '',
    )
    const [notesStr, setNotesStr] = useState(line.line_notes ?? '')

    useEffect(() => {
        setCountedStr(line.counted_qty != null ? String(line.counted_qty) : '')
    }, [line.counted_qty])

    const parsedCounted =
        countedStr === '' ? null : Number.parseFloat(countedStr)
    const currentVariance =
        parsedCounted != null && !Number.isNaN(parsedCounted)
            ? parsedCounted - line.expected_qty
            : 0

    const isVariance = parsedCounted != null && currentVariance !== 0
    const varianceRatio =
        line.expected_qty > 0
            ? Math.abs(currentVariance) / line.expected_qty
            : 0
    const isWarning = isVariance && varianceRatio > WARNING_THRESHOLD

    const buildPayload = (
        countedOverride?: number | null,
        notesOverride?: string,
    ) => {
        const notesRaw = notesOverride ?? notesStr
        const countedRaw = countedOverride === undefined ? countedStr : ''
        const counted =
            countedOverride !== undefined
                ? countedOverride
                : countedRaw === ''
                  ? null
                  : Number.parseFloat(countedRaw)
        return {
            counted_qty:
                counted === null || Number.isNaN(counted) ? null : counted,
            line_notes: notesRaw.trim() === '' ? null : notesRaw,
        }
    }

    const handleCountedBlur = () => {
        if (isWarning) workflow.step('warning_shown')
        const val = countedStr === '' ? null : Number.parseFloat(countedStr)
        if (val !== null && Number.isNaN(val)) return
        onChange(buildPayload())
    }

    const handleFillExpected = (value: number) => {
        setCountedStr(String(value))
        onChange(buildPayload(value))
    }

    const handleNotesBlur = () => {
        const val = countedStr === '' ? null : Number.parseFloat(countedStr)
        if (val !== null && Number.isNaN(val)) return
        onChange(buildPayload())
    }

    return (
        <div
            className="rounded-md border bg-card p-3 space-y-2"
            data-lot-row-id={line.id}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">
                            #{index + 1}
                        </span>
                        <span className="font-mono font-medium break-all">
                            {line.lot_code}
                        </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {line.warehouse_name}
                        {line.material_or_product_name
                            ? ` · ${line.material_or_product_name}`
                            : ''}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">
                        {m.stocktake_line_col_system_qty()}
                    </div>
                    <div className="text-base font-semibold tabular-nums">
                        {formatQty(line.expected_qty)}
                    </div>
                    {line.delta_after_snapshot !== 0 && (
                        <span
                            className={`text-xs ${
                                line.delta_after_snapshot > 0
                                    ? 'text-green-700'
                                    : 'text-red-700'
                            }`}
                        >
                            ({formatSigned(line.delta_after_snapshot)})
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                        {m.stocktake_line_col_counted()}
                    </label>
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step="0.001"
                            min={0}
                            value={countedStr}
                            onChange={(e) => setCountedStr(e.target.value)}
                            onBlur={handleCountedBlur}
                            disabled={!editable}
                            className="h-11 text-right tabular-nums"
                        />
                        {editable && (
                            <QuickFillButton
                                expected={line.expected_qty}
                                onFill={handleFillExpected}
                            />
                        )}
                    </div>
                </div>
                <div className="pt-5 text-right">
                    <div className="text-xs text-muted-foreground">
                        {m.stocktake_line_col_variance()}
                    </div>
                    {isVariance ? (
                        <div className="flex items-center justify-end gap-1">
                            <span
                                className={
                                    currentVariance > 0
                                        ? 'text-green-700 font-semibold tabular-nums'
                                        : 'text-red-700 font-semibold tabular-nums'
                                }
                            >
                                {formatSigned(currentVariance)}
                            </span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">—</span>
                    )}
                    {isWarning && (
                        <Badge
                            variant="outline"
                            className="border-amber-400 text-amber-700 bg-amber-50 mt-1"
                        >
                            {m.stocktake_variance_warning()}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Notes luôn hiển thị, enable trừ khi read-only. Đỏ viền chỉ khi
             * variance lớn (isWarning) mà trống. */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                    {m.stocktake_line_col_notes()}
                </label>
                <Input
                    value={notesStr}
                    onChange={(e) => setNotesStr(e.target.value)}
                    onBlur={handleNotesBlur}
                    disabled={!editable}
                    placeholder={m.stocktake_line_placeholder_notes()}
                    className={cn(
                        'h-11',
                        isWarning &&
                            notesStr.trim() === '' &&
                            'border-red-500 focus-visible:ring-red-500',
                    )}
                />
            </div>
        </div>
    )
}
